import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  FileCode,
  Layers,
  Loader2,
  Save,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import PostmarkTemplatePickerModal from '../Automations/PostmarkTemplatePickerModal';
import ConfirmModal from '../Common/ConfirmModal';
import Tooltip from '../Common/Tooltip';
import PostmarkTemplatePreview from './PostmarkTemplatePreview';
import { POSTMARK_EDITOR_TEXTAREA_CLASS } from './postmarkTemplateLocalPreview';
import {
  createPostmarkTemplate,
  deletePostmarkTemplate,
  getPostmarkTemplate,
  listPostmarkTemplates,
  updatePostmarkTemplate,
} from './api';
import type {
  PostmarkTemplate,
  PostmarkTemplateDetail,
  PostmarkTemplateInput,
} from './types';

const DEFAULT_HTML_BODY =
  '<!doctype html>\n<html>\n  <body>\n    <p>Hello {{name}},</p>\n    <p>…</p>\n  </body>\n</html>';

type BodyTab = 'html' | 'text' | 'preview';

export default function PostmarkTemplateEditView() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isCreate = !routeId || routeId === 'new';

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [dupName, setDupName] = useState('');
  const [dupAlias, setDupAlias] = useState('');

  const [current, setCurrent] = useState<PostmarkTemplateDetail | null>(null);
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState(DEFAULT_HTML_BODY);
  const [textBody, setTextBody] = useState('');
  const [layoutTemplate, setLayoutTemplate] = useState('');
  const [templateType, setTemplateType] = useState<'Standard' | 'Layout'>('Standard');

  const [bodyTab, setBodyTab] = useState<BodyTab>('html');

  // Layout picker (modal) state. Templates are lazy-loaded the first time the
  // user opens the picker so this view doesn't fan out a Postmark API call
  // every time someone edits an unrelated template.
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  const [removeLayoutConfirmOpen, setRemoveLayoutConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<PostmarkTemplate[]>([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState<string | null>(null);

  const loadAvailableTemplates = useCallback(async () => {
    setAvailableLoading(true);
    setAvailableError(null);
    try {
      const list = await listPostmarkTemplates({ includeLayouts: true });
      setAvailableTemplates(list);
    } catch (e: unknown) {
      setAvailableError(
        e instanceof Error ? e.message : 'Failed to load templates',
      );
    } finally {
      setAvailableLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (isCreate) return;
    setLoading(true);
    setLoadError(null);
    try {
      const t = await getPostmarkTemplate(routeId!);
      setCurrent(t);
      setName(t.name);
      setAlias(t.alias ?? '');
      setSubject(t.subject ?? '');
      setHtmlBody(t.htmlBody ?? '');
      setTextBody(t.textBody ?? '');
      setLayoutTemplate(t.layoutTemplate ?? '');
      setTemplateType(t.templateType === 'Layout' ? 'Layout' : 'Standard');
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [isCreate, routeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isLayout = templateType === 'Layout';

  const previewTemplate = useMemo(
    (): PostmarkTemplateInput => ({
      templateType,
      htmlBody: htmlBody.trim() ? htmlBody : null,
      textBody: textBody.trim() ? textBody : null,
      subject: isLayout ? null : subject.trim() ? subject : null,
      layoutTemplate: isLayout
        ? null
        : layoutTemplate.trim()
          ? layoutTemplate.trim()
          : null,
    }),
    [templateType, htmlBody, textBody, subject, layoutTemplate, isLayout],
  );

  // If this template already references a layout, resolve its friendly name
  // by pre-loading the templates list once. Doing it here (instead of on
  // mount unconditionally) avoids a Postmark fetch when editing a Layout or a
  // Standard that doesn't use one.
  useEffect(() => {
    if (isLayout) return;
    if (!layoutTemplate.trim()) return;
    if (availableTemplates.length > 0 || availableLoading || availableError) {
      return;
    }
    void loadAvailableTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLayout, layoutTemplate]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!isLayout && !subject.trim()) {
      toast.error('Subject is required on Standard templates');
      return;
    }
    if (!htmlBody.trim() && !textBody.trim()) {
      toast.error('Provide an HTML body, a text body, or both');
      return;
    }

    const payload: PostmarkTemplateInput = {
      name: trimmedName,
      subject: isLayout ? null : subject,
      htmlBody: htmlBody.trim() ? htmlBody : null,
      textBody: textBody.trim() ? textBody : null,
      alias: alias.trim() ? alias.trim() : null,
      templateType,
      layoutTemplate: isLayout
        ? null
        : layoutTemplate.trim()
          ? layoutTemplate.trim()
          : null,
    };

    setSaving(true);
    try {
      if (isCreate) {
        const created = await createPostmarkTemplate(payload);
        toast.success('Template created');
        navigate(`/postmark-templates/${created.templateId}`, { replace: true });
        setCurrent(created);
      } else {
        const updated = await updatePostmarkTemplate(routeId!, payload);
        setCurrent(updated);
        toast.success('Template saved');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const performDelete = async () => {
    if (!current) return;
    setDeleting(true);
    try {
      await deletePostmarkTemplate(current.templateId);
      toast.success('Template deleted');
      // Leave the modal open until navigation kicks in so the spinner stays
      // visible; the view unmounts on navigate so resetting state is moot.
      navigate('/postmark-templates');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const openDuplicate = () => {
    if (!current) return;
    setDupName(current.name ? `${current.name} (copy)` : 'Copy of template');
    setDupAlias(current.alias ? `${current.alias}-copy` : '');
    setDuplicateOpen(true);
  };

  const handleDuplicate = async () => {
    if (!current) return;
    const trimmedName = dupName.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    const trimmedAlias = dupAlias.trim();
    const payload: PostmarkTemplateInput = {
      name: trimmedName,
      subject: isLayout ? null : current.subject,
      htmlBody: current.htmlBody,
      textBody: current.textBody,
      alias: trimmedAlias ? trimmedAlias : null,
      templateType: current.templateType === 'Layout' ? 'Layout' : 'Standard',
      layoutTemplate: isLayout ? null : current.layoutTemplate,
    };
    setDuplicating(true);
    try {
      const created = await createPostmarkTemplate(payload);
      toast.success('Template duplicated');
      setDuplicateOpen(false);
      navigate(`/postmark-templates/${created.templateId}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  const busy = saving || deleting || duplicating;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-7xl mx-auto py-12">
        <button
          onClick={() => navigate('/postmark-templates')}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to templates
        </button>
        <div className="p-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          Could not load template: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/postmark-templates')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to templates
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 truncate">
            <FileCode className="w-7 h-7 text-emerald-500 shrink-0" />
            {isCreate ? 'New Postmark template' : name || '(unnamed template)'}
          </h1>
          {current ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono">
              id: {current.templateId}
              {current.alias ? ` · alias: ${current.alias}` : ''}
              {current.associatedServerId
                ? ` · server: ${current.associatedServerId}`
                : ''}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!isCreate ? (
            <>
              <button
                onClick={openDuplicate}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 rounded-lg transition-colors"
                title="Create a copy of this template"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 rounded-lg transition-colors"
                title="Delete this template on Postmark"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </>
          ) : null}
          <button
            onClick={() => void handleSave()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Create template' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
              Details
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Welcome Email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Alias{' '}
                <span className="text-gray-400 dark:text-gray-500 font-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="e.g. welcome-email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Stable string identifier. Letters, numbers, and hyphens.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={templateType}
                onChange={(e) =>
                  setTemplateType(e.target.value === 'Layout' ? 'Layout' : 'Standard')
                }
                disabled={!isCreate}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
              >
                <option value="Standard">Standard</option>
                <option value="Layout">Layout</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isCreate
                  ? 'Layouts wrap other templates and don\u2019t need a subject.'
                  : 'Type is fixed once a template is created on Postmark.'}
              </p>
            </div>

            {!isLayout ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Layout template{' '}
                  <span className="text-gray-400 dark:text-gray-500 font-normal">
                    (optional)
                  </span>
                </label>
                {(() => {
                  const trimmed = layoutTemplate.trim();
                  const usingLayout = trimmed.length > 0;
                  const selected = usingLayout
                    ? availableTemplates.find(
                        (t) =>
                          t.templateType === 'Layout' &&
                          (t.alias === trimmed || String(t.templateId) === trimmed),
                      ) ?? null
                    : null;
                  const openPicker = () => {
                    setLayoutPickerOpen(true);
                    if (
                      availableTemplates.length === 0 &&
                      !availableLoading
                    ) {
                      void loadAvailableTemplates();
                    }
                  };
                  return (
                    <>
                      <div className="flex items-stretch gap-2">
                        <div
                          className={`flex-1 px-3 py-2 border rounded-lg text-sm flex items-center gap-2 min-h-[38px] ${
                            usingLayout
                              ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10'
                              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30'
                          }`}
                        >
                          {usingLayout ? (
                            <>
                              <Layers className="w-4 h-4 text-emerald-500 shrink-0" />
                              <div className="min-w-0 flex-1">
                                {selected ? (
                                  <>
                                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {selected.name || '(unnamed layout)'}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                                      {selected.alias
                                        ? `alias: ${selected.alias}`
                                        : `id: ${selected.templateId}`}
                                    </div>
                                  </>
                                ) : availableLoading ? (
                                  <span className="text-gray-500 dark:text-gray-400">
                                    Looking up layout{' '}
                                    <span className="font-mono">{trimmed}</span>…
                                  </span>
                                ) : (
                                  <span className="text-gray-700 dark:text-gray-200 font-mono break-all">
                                    {trimmed}{' '}
                                    <span className="text-xs text-amber-600 dark:text-amber-400 font-sans">
                                      (not found on this server)
                                    </span>
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 italic">
                              No template
                            </span>
                          )}
                        </div>
                        {!usingLayout ? (
                          <button
                            type="button"
                            onClick={openPicker}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                            title="Choose layout"
                            aria-label="Choose layout"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                      {usingLayout ? (
                        <div className="mt-1.5 flex items-center justify-end gap-3 text-xs">
                          <Tooltip label="Pick a different layout for this template">
                            <button
                              type="button"
                              onClick={openPicker}
                              className="text-emerald-600 dark:text-emerald-400 hover:underline"
                            >
                              Change
                            </button>
                          </Tooltip>
                          <span
                            className="text-gray-300 dark:text-gray-600"
                            aria-hidden="true"
                          >
                            ·
                          </span>
                          <Tooltip label="Detach this layout — the template will stand alone">
                            <button
                              type="button"
                              onClick={() => setRemoveLayoutConfirmOpen(true)}
                              className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:underline"
                            >
                              Remove
                            </button>
                          </Tooltip>
                        </div>
                      ) : null}
                    </>
                  );
                })()}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Wraps this template with the selected Postmark layout. Leave
                  empty to stand alone.
                </p>
                {availableError ? (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    Could not load layouts: {availableError}
                  </p>
                ) : null}
              </div>
            ) : null}

            {current ? (
              <div className="pt-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                <div>
                  Status:{' '}
                  {current.active ? (
                    <span className="font-medium text-emerald-600 dark:text-emerald-300">
                      active
                    </span>
                  ) : (
                    <span className="font-medium text-gray-600 dark:text-gray-300">
                      inactive
                    </span>
                  )}
                </div>
                <p className="mt-2">
                  Activation is managed in the Postmark dashboard.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="md:col-span-2 space-y-4">
          {!isLayout ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Welcome to {{product_name}}!"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Mustachio syntax: <code className="font-mono">{'{{variable}}'}</code>.
              </p>
            </div>
          ) : null}

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 pt-3">
              <div className="flex items-center gap-1">
                <TabButton
                  active={bodyTab === 'html'}
                  onClick={() => setBodyTab('html')}
                  label="HTML body"
                />
                <TabButton
                  active={bodyTab === 'text'}
                  onClick={() => setBodyTab('text')}
                  label="Text body"
                />
                <TabButton
                  active={bodyTab === 'preview'}
                  onClick={() => setBodyTab('preview')}
                  label="Preview"
                />
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {bodyTab === 'preview'
                  ? 'Local HTML'
                  : bodyTab === 'html'
                    ? `${htmlBody.length} chars`
                    : `${textBody.length} chars`}
              </span>
            </div>
            <div className="p-5">
              {bodyTab === 'html' ? (
                <textarea
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  spellCheck={false}
                  placeholder="<html>…</html>"
                  className={POSTMARK_EDITOR_TEXTAREA_CLASS}
                />
              ) : bodyTab === 'text' ? (
                <textarea
                  value={textBody}
                  onChange={(e) => setTextBody(e.target.value)}
                  spellCheck={false}
                  placeholder="Plain-text fallback (recommended)"
                  className={POSTMARK_EDITOR_TEXTAREA_CLASS}
                />
              ) : (
                <PostmarkTemplatePreview template={previewTemplate} />
              )}
              {bodyTab !== 'preview' ? (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Provide at least one of HTML or text. Postmark serves the matching part
                  based on the recipient&rsquo;s client.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {duplicateOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            if (!duplicating) setDuplicateOpen(false);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Copy className="w-5 h-5 text-emerald-500" />
                Duplicate template
              </h2>
              <button
                onClick={() => setDuplicateOpen(false)}
                disabled={duplicating}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Creates a new template on Postmark with the same subject and body. The
                original is left untouched.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={dupName}
                  onChange={(e) => setDupName(e.target.value)}
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New alias{' '}
                  <span className="text-gray-400 dark:text-gray-500 font-normal">
                    (optional)
                  </span>
                </label>
                <input
                  type="text"
                  value={dupAlias}
                  onChange={(e) => setDupAlias(e.target.value)}
                  placeholder="e.g. welcome-email-v2"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Lowercase letters, digits, and hyphens. Must be unique on the server.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setDuplicateOpen(false)}
                disabled={duplicating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDuplicate()}
                disabled={duplicating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
              >
                {duplicating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PostmarkTemplatePickerModal
        isOpen={layoutPickerOpen}
        onClose={() => setLayoutPickerOpen(false)}
        templates={availableTemplates}
        loading={availableLoading}
        error={availableError}
        onRefresh={() => void loadAvailableTemplates()}
        currentValue={layoutTemplate.trim() || null}
        mode="layouts"
        onSelect={(t) => {
          // Layouts are referenced by alias on Postmark. We only show aliased
          // layouts in the picker, so this should always be a string.
          setLayoutTemplate(t.alias ?? String(t.templateId));
        }}
      />

      <ConfirmModal
        isOpen={removeLayoutConfirmOpen}
        title="Remove layout?"
        description="This template will stand alone and won't be wrapped by a layout on send."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => {
          setLayoutTemplate('');
          setRemoveLayoutConfirmOpen(false);
        }}
        onClose={() => setRemoveLayoutConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title={`Delete "${current?.name || '(unnamed)'}"?`}
        description={
          <>
            <p>
              This deletes the template on Postmark. It cannot be undone.
            </p>
            {current?.alias ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
                alias: {current.alias}
              </p>
            ) : null}
          </>
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        busy={deleting}
        onConfirm={() => void performDelete()}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-emerald-500 text-emerald-600 dark:text-emerald-300'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
